import React from "react";
import { motion } from "motion/react";

// Directional distances for each reveal style
const VARIANTS = {
  up: { hidden: { opacity: 0, y: 42 }, show: { opacity: 1, y: 0 } },
  down: { hidden: { opacity: 0, y: -42 }, show: { opacity: 1, y: 0 } },
  left: { hidden: { opacity: 0, x: 48 }, show: { opacity: 1, x: 0 } },
  right: { hidden: { opacity: 0, x: -48 }, show: { opacity: 1, x: 0 } },
  zoom: { hidden: { opacity: 0, scale: 0.88 }, show: { opacity: 1, scale: 1 } },
  fade: { hidden: { opacity: 0 }, show: { opacity: 1 } },
};

/**
 * Scroll-triggered reveal wrapper. Drop around any section/card to animate
 * it into view once, the first time it crosses the viewport.
 */
export default function Reveal({
  children,
  as = "div",
  direction = "up",
  delay = 0,
  duration = 0.65,
  // Deliberately small: `amount` is the fraction of THIS element that must be
  // visible to trigger. An element much taller than the viewport can never
  // reach a large fraction, which would leave it stuck at opacity 0 forever
  // (worst case: long single-column grids on mobile). 0.05 keeps that safe.
  amount = 0.05,
  once = true,
  className = "",
  ...rest
}) {
  const MotionTag = motion[as] || motion.div;
  const variant = VARIANTS[direction] || VARIANTS.up;

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
      variants={variant}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/**
 * Wraps a list of children and staggers Reveal-style entrance on each.
 * Pass `stagger` to tune spacing between each child's animation start.
 */
export function RevealGroup({
  children,
  as = "div",
  className = "",
  stagger = 0.1,
  // Same reasoning as Reveal: grids are the tallest things on the page, so a
  // low threshold is what guarantees the cascade actually fires.
  amount = 0.05,
  once = true,
  ...rest
}) {
  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger } },
      }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

export function RevealItem({ children, as = "div", direction = "up", className = "", ...rest }) {
  const MotionTag = motion[as] || motion.div;
  const variant = VARIANTS[direction] || VARIANTS.up;

  return (
    <MotionTag
      className={className}
      variants={variant}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}
